import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { ReleveKm } from '../components/ReleveKm';
import { useAlertes } from '../state/alertes';
import { centimes, dateFr, isoJour, montant } from '../lib/format';
import { useMobile } from '../lib/ecran';
import type { Vehicule } from '../api/types';

const VIDE = {
  immatriculation: '', marque: '', modele: '', annee: String(new Date().getFullYear()),
  categorie: 'B — Économique', boite: 'manuelle', carburant: 'essence', km: '0',
  haute: '', basse: '', forfaitKmJour: '250', identifiantBoitier: '',
  assurance: '', controle: '', vignette: '',
};

const TYPE_DOC: Record<string, string> = {
  assurance: 'Assurance', controle_technique: 'Contrôle technique', vignette: 'Vignette', carte_grise: 'Carte grise',
};

export default function Flotte() {
  const { peut } = useAuth();
  const mobile = useMobile();
  const [v, setV] = useState<Vehicule[] | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [edite, setEdite] = useState<Vehicule | null>(null);
  const [f, setF] = useState({ ...VIDE });
  const [erreur, setErreur] = useState('');
  const [releve, setReleve] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const { recharger: rechargerAlertes } = useAlertes();

  const charger = useCallback(() => { api.get<Vehicule[]>('/vehicules').then(setV); }, []);
  useEffect(charger, [charger]);
  if (!v) return <Squelette lignes={8} />;

  function ouvrirCreation() { setEdite(null); setF({ ...VIDE }); setErreur(''); setOuvert(true); }
  function ouvrirEdition(x: Vehicule) {
    setEdite(x); setErreur('');
    const doc = (t: string) => x.documents.find((d) => d.type === t)?.dateExpiration?.slice(0, 10) ?? '';
    setF({
      immatriculation: x.immatriculation, marque: x.marque, modele: x.modele, annee: String(x.annee),
      categorie: x.categorie, boite: x.boite, carburant: x.carburant, km: String(x.km),
      haute: String(x.tarifHauteSaisonMinor / 100), basse: String(x.tarifBasseSaisonMinor / 100),
      forfaitKmJour: String(x.forfaitKmJour), identifiantBoitier: x.identifiantBoitier ?? '',
      assurance: doc('assurance'), controle: doc('controle_technique'), vignette: doc('vignette'),
    });
    setOuvert(true);
  }

  async function enregistrer() {
    setErreur('');
    // Un bouton grisé sans explication laisse l'utilisateur cliquer dans le vide.
    // On laisse cliquer, et on nomme précisément ce qui manque.
    const manquants = [
      !f.immatriculation.trim() && "l'immatriculation",
      !f.marque.trim() && 'la marque',
      !f.modele.trim() && 'le modèle',
      !Number(f.haute) && 'le tarif été',
      !Number(f.basse) && "le tarif hiver",
    ].filter(Boolean) as string[];
    if (manquants.length) {
      setErreur(`Il manque ${manquants.length > 1 ? 'encore ' : ''}${manquants.join(', ')}.`);
      return;
    }
    const documents = [
      f.assurance && { type: 'assurance', dateExpiration: f.assurance },
      f.controle && { type: 'controle_technique', dateExpiration: f.controle },
      f.vignette && { type: 'vignette', dateExpiration: f.vignette },
    ].filter(Boolean);
    const corps = {
      immatriculation: f.immatriculation, marque: f.marque, modele: f.modele, annee: Number(f.annee),
      categorie: f.categorie, boite: f.boite, carburant: f.carburant, km: Number(f.km || 0),
      tarifHauteSaisonMinor: centimes(Number(f.haute || 0)), tarifBasseSaisonMinor: centimes(Number(f.basse || 0)),
      forfaitKmJour: Number(f.forfaitKmJour || 0), identifiantBoitier: f.identifiantBoitier || undefined,
      documents,
    };
    try {
      if (edite) await api.patch(`/vehicules/${edite._id}`, corps);
      else await api.post('/vehicules', corps);
      setOuvert(false); charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  const pastille = (j: number) => j <= 7 ? 'retard' : j <= 30 ? 'retour' : 'en_cours';

  /*
   * L'entretien se lit comme un document : « Vidange · 400 km » a cote de
   * « Assurance · J-12 ». Le patron ne veut pas se demander si l'alerte vient
   * d'un papier ou du garage — c'est la meme question, la meme ligne.
   */
  const badgeEntretien = (e: Vehicule['prochainEntretien']) => {
    if (!e) return null;
    const km = e.resteKm;
    const j = e.resteJours;
    const statut = (km != null && km <= 0) || (j != null && j <= 0) ? 'retard'
      : (km != null && km < 1000) || (j != null && j < 15) ? 'retour' : 'en_cours';
    const texte = km != null
      ? (km <= 0 ? `${e.type} · dépassée` : `${e.type} · ${km.toLocaleString('fr-FR')} km`)
      : `${e.type} · J-${j}`;
    return <Badge statut={statut} libelle={texte} />;
  };

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Mes voitures</h1>
          <p>{v.length === 0
            ? 'Commencez par enregistrer vos voitures : tout le reste en depend'
            : `${v.length} voiture${v.length > 1 ? 's' : ''} · chaque voiture a son propre tarif, été et hiver`}</p>
        </div>
        <div className="actions">
          {/* Le kilometrage se releve en passant, pas seulement dans un etat des
              lieux : c'est lui qui declenche la vidange. */}
          {/* Relever un compteur est un AJOUT, pas une modification : Sabrina le
              fait aussi, mais seulement pendant les fenêtres d'inventaire. */}
          {peut('creer.kilometrage') && v.length > 0 && (
            <button className="btn" onClick={() => setReleve('')}>Relever un kilométrage</button>
          )}
          {peut('creer.vehicule') && (
            <button className="btn btn-p" onClick={ouvrirCreation}>Ajouter une voiture</button>
          )}
        </div>
      </div>

      {/* Sur téléphone, le tableau à neuf colonnes met « Modifier » hors écran :
          on présente une carte par voiture, la carte entière étant l'action. */}
      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      {v.length === 0 ? (
        <Vide titre="Aucune voiture enregistrée"
              action={peut('creer.vehicule')
                ? <button className="btn btn-p btn-l" onClick={ouvrirCreation}>Ajouter ma première voiture</button>
                : undefined}>
          Chaque voiture porte son propre tarif — un tarif d'été et un tarif d'hiver — et ses dates
          d'assurance, de vignette et de contrôle technique. Le planning, les disponibilités et les
          contrats se remplissent ensuite tout seuls à partir d'ici.
          {!peut('creer.vehicule') && <> Demandez à un gérant d'ajouter les voitures.</>}
        </Vide>
      ) : mobile ? (
        <div className="cartes">
          {v.map((x) => {
            const alertes = x.alertesDocuments ?? [];
            const pire = alertes.length ? Math.min(...alertes.map((a) => a.joursRestants)) : null;
            return (
              <button key={x._id} className="carte-ligne"
                      onClick={() => (peut('modifier.vehicule') ? ouvrirEdition(x) : undefined)}>
                <div className="cl-tete">
                  <span className="cl-titre">{x.marque} {x.modele}</span>
                  <Badge statut={x.statut} />
                </div>
                <div className="cl-sous">{x.immatriculation} · {x.annee} · {x.categorie}</div>
                {x.prochainEntretien && (
                  <div style={{ marginTop: 6 }}>{badgeEntretien(x.prochainEntretien)}</div>
                )}
                <div className="cl-champs">
                  <div className="cl-champ"><span>Tarif été</span><span>{montant(x.tarifHauteSaisonMinor)}</span></div>
                  <div className="cl-champ"><span>Tarif hiver</span><span>{montant(x.tarifBasseSaisonMinor)}</span></div>
                  <div className="cl-champ"><span>Kilométrage</span><span>{x.km.toLocaleString('fr-FR')} km</span></div>
                  <div className="cl-champ"><span>Boîte</span><span>{x.boite}</span></div>
                </div>
                {alertes.length > 0 && (
                  <div className="cl-pied">
                    {alertes.map((a) => (
                      <Badge key={a.type} statut={pastille(a.joursRestants)}
                             libelle={`${TYPE_DOC[a.type] ?? a.type} · J-${a.joursRestants}`} />
                    ))}
                  </div>
                )}
                {peut('modifier.vehicule') && (
                  <div className="mini" style={{ color: 'var(--primaire)', fontWeight: 600 }}>
                    Toucher pour modifier ›
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
      <Carte pad={false}>
        <div className="enveloppe-tbl">
          <table className="tbl">
            <thead>
              <tr><th>Véhicule</th><th>Immatriculation</th><th>Catégorie</th><th className="droite">Km</th>
                  <th className="droite">Tarif été</th><th className="droite">Tarif hiver</th>
                  <th>Documents</th><th>Statut</th><th /></tr>
            </thead>
            <tbody>
              {v.map((x) => (
                <tr key={x._id}>
                  <td><b>{x.marque} {x.modele}</b> <span className="mini sourdine">{x.annee} · {x.boite}</span></td>
                  <td className="num">{x.immatriculation}</td>
                  <td className="mini sourdine">{x.categorie}</td>
                  <td className="droite num">{x.km.toLocaleString('fr-FR')}</td>
                  <td className="droite montant" style={{ background: x.saisonCourante === 'haute' ? 'var(--accent-doux)' : undefined }}>
                    {montant(x.tarifHauteSaisonMinor)}
                  </td>
                  <td className="droite montant" style={{ background: x.saisonCourante === 'basse' ? 'var(--accent-doux)' : undefined }}>
                    {montant(x.tarifBasseSaisonMinor)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(x.alertesDocuments ?? []).map((a) => (
                        <Badge key={a.type} statut={pastille(a.joursRestants)}
                               libelle={`${TYPE_DOC[a.type] ?? a.type} · J-${a.joursRestants}`} />
                      ))}
                      {badgeEntretien(x.prochainEntretien)}
                      {(x.alertesDocuments ?? []).length === 0 && !x.prochainEntretien && (
                        <span className="mini sourdine">à jour</span>
                      )}
                    </div>
                  </td>
                  <td><Badge statut={x.statut} /></td>
                  <td className="droite">{peut('modifier.vehicule') && <button className="btn btn-s" onClick={() => ouvrirEdition(x)}>Modifier</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
      )}

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        La colonne de la saison en cours est surlignee. Une voiture qui part avec une assurance expirée,
        c'est l'accident qui coute l'entreprise — d'ou les alertes a 30, 15 et 7 jours.
        <Link to="/disponibilites" style={{ marginLeft: 8 }}>Chercher une disponibilité</Link>
      </p>

      {releve !== null && (
        <ReleveKm vehicules={v} vehiculeInitial={releve || undefined}
                  onFermer={() => setReleve(null)}
                  onFini={(m) => { setReleve(null); setMsg(m); charger(); rechargerAlertes(); }} />
      )}

      {ouvert && (
        <Modale titre={edite ? `Modifier ${edite.marque} ${edite.modele}` : 'Ajouter une voiture'} onFermer={() => setOuvert(false)}>
          <div style={{ display: 'grid', gap: 12, maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
            <Champ label="Immatriculation" aide="Format algérien à 6 chiffres : 045218-116-16">
              <input value={f.immatriculation} onChange={(e) => setF({ ...f, immatriculation: e.target.value })} placeholder="045218-116-16" />
            </Champ>
            <div className="grille g2">
              <Champ label="Marque *"><input value={f.marque} onChange={(e) => setF({ ...f, marque: e.target.value })} /></Champ>
              <Champ label="Modèle *"><input value={f.modele} onChange={(e) => setF({ ...f, modele: e.target.value })} /></Champ>
              <Champ label="Année"><input type="number" value={f.annee} onChange={(e) => setF({ ...f, annee: e.target.value })} /></Champ>
              <Champ label="Catégorie">
                <select value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })}>
                  {['A — Citadine', 'B — Économique', 'C — Compacte', 'D — Berline', 'E — SUV', 'U — Utilitaire'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Champ>
              <Champ label="Boîte">
                <select value={f.boite} onChange={(e) => setF({ ...f, boite: e.target.value })}>
                  <option value="manuelle">Manuelle</option><option value="automatique">Automatique</option>
                </select>
              </Champ>
              <Champ label="Carburant">
                <select value={f.carburant} onChange={(e) => setF({ ...f, carburant: e.target.value })}>
                  {['essence', 'diesel', 'gpl', 'hybride'].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Champ>
            </div>
            <div className="grille g2">
              <Champ label="Tarif haute saison (DZD / jour)" aide="L'ete.">
                <input type="number" value={f.haute} onChange={(e) => setF({ ...f, haute: e.target.value })} />
              </Champ>
              <Champ label="Tarif hiver (DZD / jour) *" aide="Obligatoire.">
                <input type="number" value={f.basse} onChange={(e) => setF({ ...f, basse: e.target.value })} />
              </Champ>
              <Champ label="Kilométrage actuel"><input type="number" value={f.km} onChange={(e) => setF({ ...f, km: e.target.value })} /></Champ>
              <Champ label="Forfait km / jour" aide="0 = illimite. Article 05 : 250 par defaut.">
                <input type="number" value={f.forfaitKmJour} onChange={(e) => setF({ ...f, forfaitKmJour: e.target.value })} />
              </Champ>
            </div>
            <div className="grille g3">
              <Champ label="Assurance expire le"><input type="date" value={f.assurance} onChange={(e) => setF({ ...f, assurance: e.target.value })} /></Champ>
              <Champ label="Contrôle technique"><input type="date" value={f.controle} onChange={(e) => setF({ ...f, controle: e.target.value })} /></Champ>
              <Champ label="Vignette"><input type="date" value={f.vignette} onChange={(e) => setF({ ...f, vignette: e.target.value })} /></Champ>
            </div>
            <Champ label="Identifiant du boîtier GPS" aide="A remplir le jour ou vous equiperez la flotte. Laisse vide, il ne sert a rien.">
              <input value={f.identifiantBoitier} onChange={(e) => setF({ ...f, identifiantBoitier: e.target.value })} />
            </Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>Annuler</button>
            <button className="btn btn-p" onClick={() => void enregistrer()}>
              {edite ? 'Enregistrer' : 'Ajouter la voiture'}
            </button>
          </div>
        </Modale>
      )}
    </>
  );
}
